/**
 * The trades (tech-tree spec v2, §4): labels for kinds of work. A package on a
 * job is one performer's share of one trade; the schedule of values groups
 * lines by trade in the print order; performers (NSB itself, subs, suppliers,
 * consultants) are shelved under the trades they do. Each organization gets
 * this list, as rows it edits in Settings → Trades and subs; the print code is
 * for printing and never a key.
 *
 * The list is the CSI trades table Jason gave (Jason, 2026-10-04: "I wanted you
 * to use that pdf as the model for our list"; "the csi standard should be the
 * number scheme as its industry standard and my key trades are just a
 * selection there of unless I define a unique trade specifically, like natural
 * plaster for example"): every trade of the table under its five-digit number,
 * in the sixteen divisions of MasterFormat it is laid out in (and the
 * seventeenth it adds), and the few of NSB's own the table has no row for, each
 * under its division. The admin's quick list of key trades is a selection from
 * them.
 *
 * NSB's trades from before (v0.7's divisions and the stick-frame house's) each
 * became the row of the table it is, keeping its key and print code (`KEPT`),
 * so the steps and performers on them stay; the three timber trades became one
 * of NSB's own, Timber frame.
 */

export interface TradeDef {
  key: string;
  name: string;
  /** Where the lines of projects stamped from the old template print: a convention, never a key; null takes the next. */
  code: number | null;
  /** Its number in the CSI scheme: a section of the table ("06110"), or for one of NSB's own its division ("06"); null for NSB's bucket of new scope. */
  csi: string | null;
  /** On the quick list a sub is given, until the admin says otherwise. */
  keyTrade: boolean;
  /** Kept only for the lines printed under it before (the shop and the raising, now Timber frame). */
  archived?: boolean;
}

/** The divisions of the table, by number. */
export const CSI_DIVISIONS: Readonly<Record<string, string>> = {
  '01': 'General Requirements',
  '02': 'Site Construction',
  '03': 'Concrete',
  '04': 'Masonry',
  '05': 'Metals',
  '06': 'Wood and Plastics',
  '07': 'Thermal and Moisture Protection',
  '08': 'Doors and Windows',
  '09': 'Finishes',
  '10': 'Specialties',
  '11': 'Equipment',
  '12': 'Furnishings',
  '13': 'Special Construction',
  '14': 'Conveying Systems',
  '15': 'Mechanical',
  '16': 'Electrical',
  '17': 'Instrumentation & Controls',
};

/** The table's trades, as numbered and named there. */
export const CSI_TABLE: readonly (readonly [string, string])[] = [
  ['01000', 'Architects'],
  ['01001', 'Newspapers and Publications'],
  ['01010', 'Owner / Agent / CM'],
  ['01060', 'Engineers/Consultants'],
  ['01410', 'Testing & Inspection Services'],
  ['01500', 'Temporary Facilities and Controls'],
  ['01520', 'Jobsite Trailer Rental & Lease'],
  ['01540', 'Const. Elevator/Hoist/Cranes'],
  ['01542', 'Scaffolding'],
  ['01543', 'Construction Aids'],
  ['01550', 'Traffic Control'],
  ['01560', 'Temporary Barriers, Enclosures & Fencing'],
  ['01565', 'Infection Control'],
  ['01568', 'Jobsite Security Guard Service'],
  ['01650', 'Product Delivery Requirements'],
  ['01652', 'Equipment Suppliers'],
  ['01720', 'Survey & Layout'],
  ['01730', 'Machinery & Equipment Moving (Rigging)'],
  ['01740', 'Cleaning'],
  ['02100', 'Site Remediation & HazMat Abatement'],
  ['02145', 'Groundwater Treatment Systems'],
  ['02220', 'Demolition'],
  ['02240', 'Dewatering'],
  ['02250', 'Shoring and Underpinning'],
  ['02300', 'Earthwork'],
  ['02340', 'Soil Stabilization & Erosion Control'],
  ['02360', 'Soil Treatment'],
  ['02400', 'Tunneling, Boring and Jacking'],
  ['02455', 'Driven Piles'],
  ['02500', 'Site Utilities'],
  ['02580', 'Site Utilities (Dry) - Electrical & Communications'],
  ['02590', 'Traffic Signs & Signals'],
  ['02730', 'Asphalt Concrete Paving'],
  ['02760', 'Paving Specialties'],
  ['02775', 'Site Concrete'],
  ['02780', 'Unit Pavers'],
  ['02790', 'Athletic and Recreational Surfaces & Synthetic Turf'],
  ['02815', 'Fountains & Water Features'],
  ['02820', 'Fences and Gates'],
  ['02830', 'Retaining Walls'],
  ['02870', 'Site Furnishings'],
  ['02895', 'Pavement Markings'],
  ['02900', 'Landscaping & Irrigation'],
  ['03050', 'Structural Excavation & Backfill / Backhoe Services'],
  ['03100', 'Concrete Formwork'],
  ['03150', 'Concrete Accessories'],
  ['03200', 'Concrete Reinforcement'],
  ['03250', 'Post-Tensioning'],
  ['03300', 'Cast-in-Place Concrete (Contractors)'],
  ['03310', 'Concrete Ready Mix'],
  ['03320', 'Concrete Pump'],
  ['03350', 'Concrete Finishing'],
  ['03360', 'Sand & Water Blasting'],
  ['03370', 'Pneumatically Placed Concrete (Shotcrete)'],
  ['03400', 'Precast Concrete'],
  ['03470', 'Tilt-Up Concrete'],
  ['03490', 'Glass-Fiber-Reinforced Concrete'],
  ['03500', 'Cementitious Decks and Underlayment'],
  ['03900', 'Concrete Restoration and Cleaning'],
  ['04000', 'Masonry'],
  ['04400', 'Stone/Marble (Adhered)'],
  ['04401', 'Stone/Marble (Mechanically Fastened)'],
  ['04403', 'Granite Countertops'],
  ['05090', 'Welding'],
  ['05120', 'Structural Steel'],
  ['05122', 'Structural Steel Erection'],
  ['05200', 'Metal Joists/Trusses'],
  ['05300', 'Metal Deck'],
  ['05450', 'Channel Frame Strut'],
  ['05500', 'Miscellaneous Metals'],
  ['05510', 'Metal Stairs & Ladders'],
  ['05520', 'Handrails and Railings'],
  ['05700', 'Ornamental Metal'],
  ['05800', 'Expansion Control'],
  ['06050', 'Lumber Suppliers'],
  ['06110', 'Wood Framing'],
  ['06170', 'Prefabricated Structural Wood'],
  ['06220', 'Finish Carpentry & Millwork'],
  ['06600', 'Plastic Fabrications'],
  ['07100', 'Dampproofing and Waterproofing'],
  ['07180', 'Traffic Coatings'],
  ['07210', 'Building Insulation'],
  ['07240', 'Exterior Insulation and Finish Systems (EIFS)'],
  ['07250', 'Fireproofing'],
  ['07410', 'Metal Roof and Wall Panels'],
  ['07500', 'Roofing'],
  ['07600', 'Flashing and Sheet Metal'],
  ['07720', 'Roof Accessories'],
  ['07840', 'Firestopping'],
  ['07920', 'Joint Sealants'],
  ['08100', 'Metal Doors, Frames & Hardware'],
  ['08105', 'Doors, Frames & Hardware - Installation Only'],
  ['08150', 'Doors - Packaged (Total Doors)'],
  ['08200', 'Wood and Plastic Doors'],
  ['08300', 'Specialty Doors'],
  ['08310', 'Access Doors and Panels'],
  ['08330', 'Coiling Doors and Grilles'],
  ['08400', 'Entrances and Storefronts'],
  ['08460', 'Automatic Entrance Doors'],
  ['08500', 'Windows - Wood & Vinyl'],
  ['08600', 'Skylights'],
  ['08800', 'Glass & Glazing'],
  ['08850', 'Specialty Glazing'],
  ['09200', 'Lath & Plaster'],
  ['09260', 'Framing & Drywall'],
  ['09300', 'Tile'],
  ['09510', 'Acoustical Ceilings'],
  ['09610', 'Floor Treatment & Coatings'],
  ['09620', 'Specialty Flooring'],
  ['09640', 'Wood Flooring'],
  ['09651', 'Resilient Flooring'],
  ['09680', 'Carpet & Resilient Flooring'],
  ['09840', 'Acoustical Wall Treatment'],
  ['09900', 'Painting & Wallcovering'],
  ['09960', 'Special Coatings (Epoxy Coatings)'],
  ['09970', 'Intumescent Fireproofing'],
  ['10000', 'Specialties'],
  ['10100', 'Chalkboards & Markerboards'],
  ['10150', 'Toilet Partitions & Compartments'],
  ['10190', 'Cubicle Track & Curtains'],
  ['10200', 'Louvers & Vents'],
  ['10260', 'Wall & Corner Guards'],
  ['10270', 'Access Flooring'],
  ['10350', 'Flagpoles'],
  ['10400', 'Signage'],
  ['10500', 'Lockers'],
  ['10520', 'Fire Extinguishers/Cabinets'],
  ['10536', 'Fabric/Canvas Awnings Canopies'],
  ['10550', 'Postal Specialties'],
  ['10605', 'Wire Mesh Partitions'],
  ['10650', 'Operable Partitions'],
  ['10670', 'Storage Shelving'],
  ['10705', 'Sun Control Devices'],
  ['10750', 'Telephone Specialties'],
  ['10800', 'Toilet & Bath Accessories'],
  ['11010', 'Window Washing Equipment'],
  ['11060', 'Theater and Stage Equipment'],
  ['11100', 'Retail Fixtures & Showcases'],
  ['11110', 'Commercial Laundry & Dry Cleaning'],
  ['11130', 'Audio-Visual Equipment'],
  ['11150', 'Parking Control Equipment'],
  ['11160', 'Loading Dock Equipment'],
  ['11400', 'Food Service Equipment'],
  ['11450', 'Residential Laundry/Kitchen Equipment'],
  ['11480', 'Athletic, Recreational and Therapeutic Equipment'],
  ['11600', 'Laboratory Equipment'],
  ['11605', 'Lab Equipment Salvage'],
  ['11700', 'Medical Equipment'],
  ['12300', 'Lab & Medical Casework Systems'],
  ['12320', 'Manufactured Wood Casework'],
  ['12480', 'Rugs and Mats'],
  ['12490', 'Window Treatments'],
  ['12500', 'Furniture'],
  ['12600', 'Multiple Seating'],
  ['13033', 'Environmentally Controlled Rooms'],
  ['13040', 'Clean Rooms'],
  ['13090', 'Radiation Protection'],
  ['13120', 'Pre-Engineered Structures'],
  ['13150', 'Swimming Pools and Spas'],
  ['13190', 'Tennis Court Construction'],
  ['13600', 'Solar and Wind Energy Equipment'],
  ['14200', 'Elevators'],
  ['14300', 'Escalators and Moving Walks'],
  ['14400', 'Lifts'],
  ['14550', 'Conveyors'],
  ['14560', 'Chutes'],
  ['14580', 'Pneumatic Tube Systems'],
  ['14600', 'Hoists and Cranes'],
  ['15200', 'Process Piping'],
  ['15210', 'Medical Gases'],
  ['15222', 'High Purity QAQC'],
  ['15300', 'Fire Protection'],
  ['15365', 'Pre-Action Fire Suppression'],
  ['15400', 'Plumbing'],
  ['15500', 'Heating Ventilating Air Conditioning'],
  ['15900', 'Testing, Adjusting and Balancing'],
  ['15935', 'Building Systems Controls'],
  ['16000', 'Electrical'],
  ['16700', 'Communications'],
  ['16710', 'Fire Alarm Systems'],
  ['16720', 'Security Systems'],
  ['16800', 'Audio Visual System'],
  ['17000', 'Instrumentation & Controls'],
];

/**
 * The keys and print codes NSB's trades had before they came onto the table
 * (2026-10-04), kept: a trade's key is permanent (steps, performers and lines
 * name it) and its code is where it prints. Each became the table's row of its
 * five-digit number, or one of NSB's own; the shop and the raising went into
 * Timber frame and stay as archived rows.
 */
const KEPT: Readonly<Record<string, { code: number; csi: string | null }>> = {
  'general-conditions': { code: 100, csi: '01' },
  feasibility: { code: 200, csi: '01720' },
  'design-engineering': { code: 300, csi: '01060' },
  permitting: { code: 400, csi: '01' },
  'site-foundations': { code: 500, csi: '03300' },
  timber: { code: 600, csi: '06' },
  shop: { code: 700, csi: '06' },
  raising: { code: 800, csi: '06' },
  'roof-weather-in': { code: 900, csi: '07500' },
  'enclosure-insulation': { code: 1000, csi: '07210' },
  'exterior-cladding-trim': { code: 1100, csi: '07' },
  'doors-windows': { code: 1200, csi: '08500' },
  electrical: { code: 1300, csi: '16000' },
  plumbing: { code: 1400, csi: '15400' },
  hvac: { code: 1500, csi: '15500' },
  'interior-finishes': { code: 1600, csi: '09260' },
  'flooring-tile': { code: 1700, csi: '09640' },
  millwork: { code: 1800, csi: '06220' },
  specialties: { code: 1900, csi: '10000' },
  'site-improvements': { code: 2000, csi: '02900' },
  closeout: { code: 2100, csi: '01' },
  'new-scope': { code: 2200, csi: '01' },
  'utility-hookup': { code: 2300, csi: '02500' },
  'low-voltage': { code: 2400, csi: '16700' },
  masonry: { code: 2500, csi: '04000' },
  countertops: { code: 2600, csi: '04403' },
  appliances: { code: 2700, csi: '11450' },
};
const ARCHIVED: readonly { key: string; name: string }[] = [
  { key: 'shop', name: 'Shop — layout, joinery, finishing' },
  { key: 'raising', name: 'Raising and frame erection' },
];

/**
 * NSB's own trades, the work the table has no row for, each in the division it
 * belongs to and marked as custom wherever it is listed (Jason, 2026-10-04:
 * "put custom ones in the logical division with a clear indication").
 */
const NSB_OWN: readonly { key: string; name: string; csi: string }[] = [
  { key: 'general-conditions', name: 'General conditions and administration', csi: '01' },
  { key: 'permitting', name: 'Permitting and fees', csi: '01' },
  { key: 'closeout', name: 'Closeout and warranty', csi: '01' },
  { key: 'new-scope', name: 'New scope additions', csi: '01' },
  // The well and the septic the base tree's steps hire (Jason, 2026-10-07, Q5: "Add them to NSB's own trades").
  { key: 'well-drilling-and-installation', name: 'Well drilling and installation', csi: '02' },
  { key: 'septic-systems', name: 'Septic systems', csi: '02' },
  { key: 'timber', name: 'Timber frame', csi: '06' },
  { key: 'exterior-cladding-trim', name: 'Exterior cladding and trim', csi: '07' },
  { key: 'natural-plaster', name: 'Natural plaster', csi: '09' },
];

/** The quick list to start from: the trades NSB's packages and documented jobs hire, as a sub, supplier or consultant. */
export const DEFAULT_KEY_TRADES: ReadonlySet<string> = new Set([
  'design-engineering',
  'feasibility',
  'demolition',
  'earthwork',
  'utility-hookup',
  'site-improvements',
  'site-foundations',
  'masonry',
  'countertops',
  'timber',
  'wood-framing',
  'millwork',
  'exterior-cladding-trim',
  'enclosure-insulation',
  'roof-weather-in',
  'doors-windows',
  'natural-plaster',
  'interior-finishes',
  'tile',
  'flooring-tile',
  'painting-and-wallcovering',
  'appliances',
  'manufactured-wood-casework',
  'plumbing',
  'hvac',
  'electrical',
  'low-voltage',
  // The septic the base tree's steps hire (Jason, 2026-10-07, Configuring the Steps: "need a septic trade for this one, key trade").
  'septic-systems',
]);

/** A key for a trade's name: lower case, words joined by hyphens. */
export function tradeSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'trade'
  );
}

const keptKeyOf = new Map(Object.entries(KEPT).flatMap(([key, t]) => (t.csi && t.csi.length === 5 ? [[t.csi, key] as const] : [])));

/** The keys of the table's own rows: each keeps the table's number and name. Any other trade is custom, NSB's own. */
export const CSI_TABLE_KEYS: ReadonlySet<string> = new Set(CSI_TABLE.map(([csi, name]) => keptKeyOf.get(csi) ?? tradeSlug(name)));

/** A trade of NSB's own rather than a row of the CSI trades table. */
export const isCustomTrade = (key: string) => !CSI_TABLE_KEYS.has(key);

/** Every trade an organization starts with: the table's, then NSB's own, in the order of their numbers. */
export const NSB_TRADES: readonly TradeDef[] = [
  ...CSI_TABLE.map(([csi, name]) => {
    const key = keptKeyOf.get(csi) ?? tradeSlug(name);
    return { key, name, code: KEPT[key]?.code ?? null, csi, keyTrade: DEFAULT_KEY_TRADES.has(key) };
  }),
  ...NSB_OWN.map((t) => ({ ...t, code: KEPT[t.key]?.code ?? null, keyTrade: DEFAULT_KEY_TRADES.has(t.key) })),
  ...ARCHIVED.map((t) => ({ ...t, code: KEPT[t.key]!.code, csi: '06', keyTrade: false, archived: true })),
].sort((a, b) => csiOrder(a.csi, b.csi));

/**
 * A CSI number as it is written here: a division ("06"), or a section of the
 * table ("06110"; four digits are read with the division's leading zero). Any
 * other shape, nothing.
 */
export function csiNumber(raw: string | null | undefined): string | null {
  const digits = (raw ?? '').replace(/[\s.\-–]/g, '');
  if (/^\d{1,2}$/.test(digits)) return digits.padStart(2, '0');
  if (/^\d{4,5}$/.test(digits)) return digits.padStart(5, '0');
  return null;
}

/** The division a CSI number falls in, with its name in the table. */
export function csiDivision(csi: string | null): { number: string; name: string | null } | null {
  if (!csi) return null;
  const number = csi.slice(0, 2);
  return { number, name: CSI_DIVISIONS[number] ?? null };
}

/** The order trades print in: by CSI number (a division before its sections), a trade with none last. */
export function csiOrder(a: string | null, b: string | null): number {
  return (a ?? '99').localeCompare(b ?? '99');
}

export const TRADE_KEYS: ReadonlySet<string> = new Set(NSB_TRADES.map((t) => t.key));

/**
 * The order a list of trades is shown and picked in, wherever it is (L-15, Jason, 2026-10-07: "put organizational
 * custom trades at the top, then key trades then the rest"): the organization's own trades first, then the key
 * trades, then every other, each group in print order (the CSI numbers). It orders the list on screen alone: a print
 * code never changes.
 */
export const TRADE_GROUPS = ['own', 'key', 'rest'] as const;
export type TradeGroup = (typeof TRADE_GROUPS)[number];

/** Each group in a word, as a list's heading or a picker's group names it. */
export const TRADE_GROUP_WORDS: Readonly<Record<TradeGroup, string>> = {
  own: 'NSB’s own',
  key: 'Key trades',
  rest: 'Every other trade',
};

/** What the order reads of a trade: a trade row or a trade as the API sends it. */
export interface ListedTrade {
  key: string;
  name: string;
  csi: string | null;
  keyTrade: boolean;
  printOrder?: number;
}

/** Which group a trade is listed in: one of the organization's own (whether key or not), a key trade, or the rest. */
export const tradeGroupOf = (t: ListedTrade): TradeGroup => (isCustomTrade(t.key) ? 'own' : t.keyTrade ? 'key' : 'rest');

/** The comparator of that order: the group, then the CSI number, then the print order, then the name. */
export function tradeOrder(a: ListedTrade, b: ListedTrade): number {
  return (
    TRADE_GROUPS.indexOf(tradeGroupOf(a)) - TRADE_GROUPS.indexOf(tradeGroupOf(b)) ||
    csiOrder(a.csi, b.csi) ||
    (a.printOrder ?? 0) - (b.printOrder ?? 0) ||
    a.name.localeCompare(b.name)
  );
}

/** A copy of the list in that order. */
export const inTradeOrder = <T extends ListedTrade>(trades: readonly T[]): T[] => [...trades].sort(tradeOrder);

/** The list in that order, as its groups, leaving out a group with nothing in it. */
export function tradeGroups<T extends ListedTrade>(trades: readonly T[]): { group: TradeGroup; trades: T[] }[] {
  const sorted = inTradeOrder(trades);
  return TRADE_GROUPS.map((group) => ({ group, trades: sorted.filter((t) => tradeGroupOf(t) === group) })).filter((g) => g.trades.length > 0);
}

/** Who a performer is to the job. */
export const PERFORMER_KINDS = ['nsb', 'sub', 'supplier', 'consultant', 'owner'] as const;
export type PerformerKind = (typeof PERFORMER_KINDS)[number];

export const PERFORMER_KIND_WORDS: Record<PerformerKind, string> = {
  nsb: 'NSB',
  sub: 'A sub',
  supplier: 'A supplier',
  consultant: 'A consultant',
  owner: 'The owner',
};
